import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { SocialShare } from "@/components/SocialShare";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Seo, { stripHtml } from "@/components/Seo";
import {
  Loader2, PlayCircle, Lock, CheckCircle2, Circle, Paperclip, Users, Layers,
  ArrowLeft, ArrowRight, MessageSquare, Send, Trash2, GraduationCap, Award, Star,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toEmbedUrl } from "@/lib/video";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { money } from "@/lib/format";
import ListingDescription from "@/components/ListingDescription";

import RichContent from "@/components/RichContent";
export default function CourseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [activeLesson, setActiveLesson] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  const { data: course, isLoading } = useQuery({ queryKey: ["course", id], queryFn: () => api.get(`/courses/${id}`).then((r) => r.data) });

  useEffect(() => {
    if (course && !activeLesson) {
      const first = course.modules.flatMap((m) => m.lessons).find((l) => !l.locked) || course.modules[0]?.lessons[0];
      if (first) setActiveLesson(first);
    }
  }, [course]); // eslint-disable-line

  if (isLoading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-[#144F75]" size={32} /></div>;
  if (!course) return <div className="min-h-[60vh] grid place-items-center text-[#4A5D6B]">Course not found.</div>;

  const enroll = async () => {
    if (!user) { nav("/login"); return; }
    setEnrolling(true);
    try {
      const { data } = await api.post(`/courses/${course.id}/enroll`, { origin_url: window.location.origin });
      if (data.url) { window.location.href = data.url; return; }
      toast.success("You're enrolled!");
      qc.invalidateQueries({ queryKey: ["course", id] });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(formatApiError(d) || "Could not enroll");
    } finally { setEnrolling(false); }
  };

  const toggleComplete = async (lesson) => {
    try {
      const { data } = await api.post(`/courses/${course.id}/lessons/${lesson.id}/complete`, { completed: !lesson.completed });
      qc.setQueryData(["course", id], (old) => ({
        ...old, my_progress: data.progress_pct,
        modules: old.modules.map((m) => ({ ...m, lessons: m.lessons.map((l) => l.id === lesson.id ? { ...l, completed: data.completed_lessons.includes(l.id) } : l) })),
      }));
      setActiveLesson((l) => l && l.id === lesson.id ? { ...l, completed: !l.completed } : l);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8" data-testid="course-detail">
      <Seo title={course.title} description={stripHtml(course.short_description || course.description || "") || `A class at Enhancing Your World.`}
        image={course.cover_image || course.image} path={`/courses/${course.slug || course.id}`}
        jsonLd={{ "@context": "https://schema.org", "@type": "Course", name: course.title,
          description: stripHtml(course.short_description || course.description || "", 300) || undefined,
          image: course.cover_image || course.image || undefined,
          provider: { "@type": "Organization", name: "Enhancing Your World" } }} />
      <div className="flex items-center justify-between gap-3 mb-4">
        <Link to="/courses" className="text-sm text-[#4A5D6B] hover:text-[#144F75] flex items-center gap-1"><ArrowLeft size={15} /> All courses</Link>
        <SocialShare path={`/courses/${course.slug || course.id}`} ogPath={`/api/share/class/${course.slug || course.id}`} title={course.title} testid="class-share" label="Share class" />
      </div>

      {!course.enrolled ? (
        <CoursePreview course={course} onEnroll={enroll} enrolling={enrolling} setActive={setActiveLesson} activeLesson={activeLesson} />
      ) : (
        <div className="grid lg:grid-cols-[1fr_340px] gap-8">
          <div>
            {course.my_progress >= 100 && (
              <div className="eyw-card p-5 mb-5 flex items-center justify-between bg-[#5C8A72]/[0.06] border-[#5C8A72]/30" data-testid="course-completed-banner">
                <div className="flex items-center gap-3">
                  <Award className="text-[#5C8A72]" size={28} />
                  <div>
                    <p className="font-serif text-lg text-[#1A242B]">Course complete!</p>
                    <p className="text-sm text-[#4A5D6B]">Your certificate is ready.</p>
                  </div>
                </div>
                <Link to={`/courses/${id}/certificate`} data-testid="view-certificate" className="eyw-btn !py-2 text-sm"><Award size={15} /> View certificate</Link>
              </div>
            )}
            <LessonPlayer lesson={activeLesson} courseId={course.id} user={user} onToggle={toggleComplete} />
            <CourseReviews course={course} enrolled />
          </div>
          <aside>
            <div className="eyw-card p-5 mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="eyw-label">Your progress</p>
                <span className="text-sm font-semibold text-[#144F75]">{course.my_progress}%</span>
              </div>
              <div className="h-2 rounded-full bg-[#E5E0D8] overflow-hidden"><span className="block h-full bg-[#5C8A72] transition-all" style={{ width: `${course.my_progress}%` }} /></div>
            </div>
            <Curriculum course={course} activeId={activeLesson?.id} onSelect={setActiveLesson} />
          </aside>
        </div>
      )}
    </div>
  );
}

function CoursePreview({ course, onEnroll, enrolling, setActive, activeLesson }) {
  const isMembersLocked = course.access === "membership" && !course.membership_eligible;
  const isSub = course.access === "paid" && course.billing_type === "subscription";
  const subSuffix = isSub ? ` / ${course.sub_interval_count > 1 ? `${course.sub_interval_count} ${course.sub_interval}s` : course.sub_interval}` : "";
  const cta = course.access === "free" ? "Enroll free" : course.access === "membership" ? (course.membership_eligible ? "Start course" : "Members only") : `${isSub ? "Subscribe" : "Enroll"} · ${money((course.price || 0))}${subSuffix}`;
  const priceLabel = course.access === "free" ? "Free" : course.access === "membership" ? "Included with membership" : `${money((course.price || 0))}${subSuffix}`;
  const inst = course.instructor;
  const { data: allTestimonials = [] } = useQuery({ queryKey: ["testimonials"], queryFn: () => api.get("/testimonials").then((r) => r.data) });
  const testimonials = allTestimonials
    .filter((t) => t.published && (t.status || "approved") === "approved")
    .filter((t) => t.subject_name === course.title || t.featured || t.subject_type === "ecosystem")
    .sort((a, b) => (b.subject_name === course.title) - (a.subject_name === course.title))
    .slice(0, 3);

  const CtaButton = ({ testid, full }) => (
    <button onClick={onEnroll} disabled={enrolling || isMembersLocked} data-testid={testid}
      className={`eyw-btn justify-center disabled:opacity-60 ${full ? "w-full" : ""}`}>
      {enrolling && <Loader2 className="animate-spin" size={16} />} {cta} {!enrolling && <ArrowRight size={16} />}
    </button>
  );

  return (
    <div className="pb-24 lg:pb-0" data-testid="course-preview">
      {/* ============ HERO ============ */}
      <div className="grid lg:grid-cols-[1fr_360px] gap-10">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#144F75] bg-[#144F75]/[0.07] px-3 py-1 rounded-full">{course.category}</span>
            {course.review_count > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-[#4A5D6B] bg-white border border-[#E5E0D8] px-3 py-1 rounded-full">
                <Star size={12} className="fill-[#D98E73] text-[#D98E73]" /> {course.avg_rating.toFixed(1)} · {course.review_count} review{course.review_count > 1 ? "s" : ""}
              </span>
            )}
            {course.enrolled_count > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-[#4A5D6B] bg-white border border-[#E5E0D8] px-3 py-1 rounded-full">
                <Users size={12} /> {course.enrolled_count} enrolled
              </span>
            )}
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A242B] leading-tight" data-testid="course-hero-title">{course.title}</h1>
          {course.subtitle && <p className="text-lg text-[#4A5D6B] mt-3 leading-relaxed max-w-2xl">{course.subtitle}</p>}
          {inst && (
            <div className="flex items-center gap-3 mt-5">
              {inst.avatar ? <img src={inst.avatar} alt={inst.name} className="h-10 w-10 rounded-full object-cover ring-2 ring-[#D98E73]/30" />
                : <span className="grid place-items-center h-10 w-10 rounded-full bg-[#144F75]/10 text-[#144F75] font-serif">{(inst.name || "?")[0]}</span>}
              <p className="text-sm text-[#4A5D6B]">Taught by <span className="font-medium text-[#1A242B]">{inst.name}</span>{inst.headline ? <span className="text-[#8FA89B]"> · {inst.headline}</span> : null}</p>
            </div>
          )}

          <div className="aspect-video bg-[#E5E0D8] rounded-2xl overflow-hidden mt-7 shadow-sm" data-testid="course-hero-media">
            {activeLesson?.video_url ? <VideoFrame url={activeLesson.video_url} /> :
              course.thumbnail ? <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" /> :
                <div className="w-full h-full grid place-items-center bg-gradient-to-br from-[#144F75] to-[#0d3550]"><PlayCircle className="text-white/70" size={48} /></div>}
          </div>

          {/* ============ DESCRIPTION ============ */}
          <section className="mt-10" data-testid="course-about">
            <p className="eyw-label mb-2">About this program</p>
            <ListingDescription text={course.description} item={course} kind="program" html className="prose max-w-none text-[#4A5D6B] leading-relaxed" testid="course-description" />
          </section>

          {/* ============ CURRICULUM ============ */}
          <section className="mt-10" data-testid="course-curriculum-section">
            <p className="eyw-label mb-1">What you'll learn</p>
            <h2 className="font-serif text-2xl text-[#1A242B] mb-1">Course curriculum</h2>
            <p className="text-sm text-[#8FA89B] mb-4">{course.modules.length} module{course.modules.length !== 1 ? "s" : ""} · {course.lesson_count} lesson{course.lesson_count !== 1 ? "s" : ""}</p>
            <div className="space-y-3">
              {course.modules.map((m, mi) => (
                <div key={m.id} className="eyw-card overflow-hidden" data-testid={`curriculum-module-${m.id}`}>
                  <div className="flex items-center gap-3 px-5 py-3.5 bg-[#F9F7F3] border-b border-[#E5E0D8]">
                    <span className={`grid place-items-center h-7 w-7 rounded-full text-xs font-semibold text-white shrink-0 ${mi % 2 === 0 ? "bg-[#5C8A72]" : "bg-[#D98E73]"}`}>{mi + 1}</span>
                    <h3 className="font-serif text-base text-[#1A242B]">{m.title || "Section"}</h3>
                    <span className="ml-auto text-xs text-[#8FA89B]">{m.lessons.length} lesson{m.lessons.length !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="divide-y divide-[#E5E0D8]/70">
                    {m.lessons.map((l) => (
                      <button key={l.id} onClick={() => !l.locked && setActive(l)} data-testid={`curriculum-lesson-${l.id}`}
                        className={`w-full flex items-center gap-3 text-left px-5 py-3 text-sm transition-colors ${activeLesson?.id === l.id ? "bg-[#144F75]/[0.06] text-[#144F75]" : "text-[#4A5D6B] hover:bg-[#F9F7F3]"} ${l.locked ? "cursor-default" : ""}`}>
                        {l.locked ? <Lock size={14} className="text-[#C9C2B6] shrink-0" /> : <PlayCircle size={15} className="text-[#5C8A72] shrink-0" />}
                        <span className={`flex-1 ${l.locked ? "text-[#8FA89B]" : ""}`}>{l.title}</span>
                        {l.duration_min > 0 && <span className="text-xs text-[#8FA89B]">{l.duration_min} min</span>}
                        {l.is_preview && <span className="text-[10px] font-semibold uppercase tracking-wide text-[#D98E73] bg-[#D98E73]/10 px-2 py-0.5 rounded-full">Preview</span>}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ============ INSTRUCTOR ============ */}
          {inst && (
            <section className="mt-10" data-testid="course-instructor">
              <p className="eyw-label mb-2">Your instructor</p>
              <div className="eyw-card p-6 md:p-8 flex flex-col sm:flex-row gap-6 items-start">
                {inst.avatar ? <img src={inst.avatar} alt={inst.name} className="h-24 w-24 rounded-full object-cover ring-4 ring-[#F9F7F3] shadow-md shrink-0" />
                  : <span className="grid place-items-center h-24 w-24 rounded-full bg-[#144F75]/10 text-[#144F75] font-serif text-3xl shrink-0">{(inst.name || "?")[0]}</span>}
                <div className="min-w-0">
                  <h3 className="font-serif text-xl text-[#1A242B]">{inst.name}</h3>
                  {inst.headline && <p className="text-sm text-[#D98E73] font-medium mt-0.5">{inst.headline}</p>}
                  {inst.credentials && <p className="text-xs text-[#8FA89B] mt-1">{inst.credentials}</p>}
                  {inst.bio && <RichContent text={inst.bio} className="text-sm text-[#4A5D6B] leading-relaxed mt-3" />}
                  <Link to={`/enhancers/${inst.id}`} className="inline-flex items-center gap-1 text-sm text-[#144F75] font-medium hover:underline mt-3" data-testid="course-instructor-link">
                    View full profile <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </section>
          )}

          {/* ============ SOCIAL PROOF ============ */}
          {testimonials.length > 0 && (
            <section className="mt-10" data-testid="course-testimonials">
              <p className="eyw-label mb-2">What our community says</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {testimonials.map((t) => (
                  <figure key={t.id} className="eyw-card p-5 flex flex-col" data-testid={`course-testimonial-${t.id}`}>
                    <div className="flex mb-2">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} className={n <= (t.rating || 5) ? "fill-[#D98E73] text-[#D98E73]" : "text-[#E5E0D8]"} />)}</div>
                    <blockquote className="text-sm text-[#4A5D6B] leading-relaxed flex-1">"{t.quote}"</blockquote>
                    <figcaption className="text-xs text-[#1A242B] font-medium mt-3">{t.name}{t.role ? <span className="text-[#8FA89B] font-normal"> · {t.role}</span> : null}</figcaption>
                  </figure>
                ))}
              </div>
            </section>
          )}

          <CourseReviews course={course} enrolled={course.enrolled} />

          {/* ============ BOTTOM CTA BANNER ============ */}
          <section className="mt-12 rounded-3xl bg-[#144F75] p-8 md:p-10 text-center relative overflow-hidden" data-testid="course-bottom-cta">
            <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, #fff 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
            <div className="relative">
              <h2 className="font-serif text-2xl md:text-3xl text-white">Ready to begin{inst ? ` with ${inst.name.split(" ")[0]}` : ""}?</h2>
              <p className="text-white/80 mt-2 text-sm max-w-md mx-auto">Join {course.enrolled_count > 0 ? `${course.enrolled_count} others` : "our community"} learning at your own pace, on any device.</p>
              <button onClick={onEnroll} disabled={enrolling || isMembersLocked} data-testid="course-enroll-bottom"
                className="eyw-btn !bg-[#D98E73] hover:!bg-[#c67a60] mt-5 disabled:opacity-60">{enrolling && <Loader2 className="animate-spin" size={16} />} {cta}</button>
            </div>
          </section>
        </div>

        {/* ============ STICKY PURCHASE CARD (desktop) ============ */}
        <aside className="hidden lg:block">
          <div className="eyw-card p-7 sticky top-24 shadow-md" data-testid="course-purchase-card">
            <div className="text-3xl font-serif text-[#1A242B]">{priceLabel}</div>
            {course.access === "paid" && course.member_discount_pct > 0 && (
              <p className="text-xs text-[#5C8A72] mt-1">Members save {course.member_discount_pct}%</p>
            )}
            <div className="mt-5"><CtaButton testid="course-enroll" full /></div>
            {isMembersLocked && <p className="text-xs text-[#B5794E] mt-2 text-center">Included with membership. <Link to="/memberships" className="underline">See plans</Link></p>}
            <ul className="mt-6 space-y-2.5 text-sm text-[#4A5D6B]">
              <li className="flex items-center gap-2.5"><Layers size={15} className="text-[#5C8A72]" /> {course.lesson_count} lessons in {course.modules.length} module{course.modules.length !== 1 ? "s" : ""}</li>
              <li className="flex items-center gap-2.5"><PlayCircle size={15} className="text-[#5C8A72]" /> Learn at your own pace, any device</li>
              <li className="flex items-center gap-2.5"><Award size={15} className="text-[#5C8A72]" /> Certificate of completion</li>
              {course.enrolled_count > 0 && <li className="flex items-center gap-2.5"><Users size={15} className="text-[#5C8A72]" /> {course.enrolled_count} enrolled</li>}
            </ul>
            {inst && (
              <div className="flex items-center gap-3 border-t border-[#E5E0D8] mt-6 pt-5">
                {inst.avatar ? <img src={inst.avatar} alt={inst.name} className="h-9 w-9 rounded-full object-cover" />
                  : <span className="grid place-items-center h-9 w-9 rounded-full bg-[#144F75]/10 text-[#144F75] font-serif text-sm">{(inst.name || "?")[0]}</span>}
                <p className="text-xs text-[#4A5D6B]">Taught by<br /><span className="font-medium text-[#1A242B]">{inst.name}</span></p>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* ============ MOBILE STICKY CTA ============ */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#E5E0D8] px-4 py-3 flex items-center justify-between gap-3 shadow-[0_-6px_20px_rgba(26,36,43,0.08)] lg:hidden" data-testid="course-mobile-cta">
        <div className="min-w-0">
          <p className="font-serif text-lg text-[#1A242B] leading-none">{priceLabel}</p>
          {course.review_count > 0 && <p className="text-[11px] text-[#8FA89B] mt-1 flex items-center gap-1"><Star size={10} className="fill-[#D98E73] text-[#D98E73]" /> {course.avg_rating.toFixed(1)} · {course.enrolled_count} enrolled</p>}
        </div>
        <button onClick={onEnroll} disabled={enrolling || isMembersLocked} data-testid="course-enroll-mobile" className="eyw-btn !py-2.5 !px-6 text-sm shrink-0 disabled:opacity-60">
          {enrolling && <Loader2 className="animate-spin" size={14} />} {cta}
        </button>
      </div>
    </div>
  );
}

function Curriculum({ course, activeId, onSelect, previewMode }) {
  return (
    <div data-testid="course-curriculum">
      {course.modules.map((m, mi) => (
        <div key={m.id} className="mb-4">
          <h4 className="font-serif text-sm text-[#1A242B] mb-2 flex items-center gap-2"><span className="text-[#8FA89B]">{mi + 1}.</span> {m.title || "Section"}</h4>
          <div className="space-y-1">
            {m.lessons.map((l) => (
              <button key={l.id} onClick={() => onSelect(l)} data-testid={`curriculum-lesson-${l.id}`}
                className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${activeId === l.id ? "bg-[#144F75]/[0.08] text-[#144F75]" : "text-[#4A5D6B] hover:bg-[#F9F7F3]"} ${l.locked ? "opacity-60" : ""}`}>
                {l.locked ? <Lock size={14} className="text-[#8FA89B]" /> : l.completed ? <CheckCircle2 size={15} className="text-[#5C8A72]" /> : <Circle size={15} className="text-[#8FA89B]" />}
                <span className="flex-1">{l.title}</span>
                {l.is_preview && !l.completed && <span className="text-[10px] text-[#D98E73]">Preview</span>}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function VideoFrame({ url }) {
  return <iframe src={toEmbedUrl(url)} title="lesson" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />;
}

function LessonPlayer({ lesson, courseId, user, onToggle }) {
  if (!lesson) return <div className="eyw-card p-10 text-center text-[#4A5D6B]">Select a lesson to begin.</div>;
  return (
    <div data-testid="lesson-player">
      <div className="aspect-video bg-black rounded-xl overflow-hidden">
        {lesson.video_url ? <VideoFrame url={lesson.video_url} /> :
          <div className="w-full h-full grid place-items-center text-white/60"><GraduationCap size={40} /></div>}
      </div>
      <div className="flex items-center justify-between mt-4">
        <h2 className="font-serif text-2xl text-[#1A242B]">{lesson.title}</h2>
        <button onClick={() => onToggle(lesson)} data-testid="lesson-complete-toggle"
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${lesson.completed ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "border border-[#144F75] text-[#144F75]"}`}>
          {lesson.completed ? <><CheckCircle2 size={16} /> Completed</> : <><Circle size={16} /> Mark complete</>}
        </button>
      </div>
      {lesson.audio_url && (
        <div className="mt-4" data-testid="lesson-audio">
          <p className="eyw-label mb-2">Listen</p>
          <audio controls src={lesson.audio_url} className="w-full" preload="none" />
        </div>
      )}
      {lesson.live_kind && (
        <div className="mt-4 rounded-xl border border-[#9E5C7F]/30 bg-[#9E5C7F]/[0.06] p-4" data-testid="lesson-live-session">
          <p className="text-xs uppercase tracking-wide font-semibold text-[#9E5C7F] mb-1.5">Live session</p>
          <p className="text-sm text-[#1A242B]">
            {lesson.live_kind === "in_person" ? `In person${lesson.live_location ? ` · ${lesson.live_location}` : ""}` : lesson.live_kind === "zoom" ? "Live on Zoom" : "Live on Google Meet"}
            {lesson.live_at && ` — ${new Date(lesson.live_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`}
          </p>
          {lesson.live_url && lesson.live_kind !== "in_person" && (
            <a href={lesson.live_url} target="_blank" rel="noreferrer" data-testid="lesson-live-join" className="eyw-btn !py-2 text-sm mt-3 inline-flex">Join live session</a>
          )}
        </div>
      )}
      {lesson.body && <RichContent text={lesson.body} className="prose max-w-none mt-4 text-[#4A5D6B] leading-relaxed" testid="lesson-body" />}
      {lesson.attachments?.length > 0 && (
        <div className="mt-5">
          <p className="eyw-label mb-2">Downloads</p>
          <div className="flex flex-wrap gap-2">
            {lesson.attachments.map((a, i) => (
              <a key={i} href={a.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm eyw-card !py-2 !px-3 hover:text-[#144F75]"><Paperclip size={14} /> {a.name}</a>
            ))}
          </div>
        </div>
      )}
      <LessonComments courseId={courseId} lessonId={lesson.id} user={user} />
    </div>
  );
}

function LessonComments({ courseId, lessonId, user }) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const { data: comments } = useQuery({ queryKey: ["comments", courseId, lessonId], queryFn: () => api.get(`/courses/${courseId}/lessons/${lessonId}/comments`).then((r) => r.data), enabled: !!lessonId });

  const post = async () => {
    if (!body.trim()) return;
    try {
      await api.post(`/courses/${courseId}/lessons/${lessonId}/comments`, { body });
      setBody("");
      qc.invalidateQueries({ queryKey: ["comments", courseId, lessonId] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (cid) => { await api.delete(`/courses/comments/${cid}`); qc.invalidateQueries({ queryKey: ["comments", courseId, lessonId] }); };

  return (
    <div className="mt-8 border-t border-[#E5E0D8] pt-6" data-testid="lesson-comments">
      <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2 mb-4"><MessageSquare size={18} /> Discussion ({comments?.length || 0})</h3>
      <div className="flex gap-2 mb-5">
        <input value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && post()} placeholder="Share a reflection or question…" data-testid="comment-input" className="eyw-input flex-1" />
        <button onClick={post} data-testid="comment-submit" className="eyw-btn !px-4"><Send size={16} /></button>
      </div>
      <div className="space-y-4">
        {(comments || []).map((c) => (
          <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
            {c.user_avatar ? <img src={c.user_avatar} alt={c.user_name} className="h-9 w-9 rounded-full object-cover" /> :
              <span className="grid place-items-center h-9 w-9 rounded-full bg-[#144F75]/10 text-[#144F75] text-sm font-semibold">{(c.user_name || "?")[0]}</span>}
            <div className="flex-1">
              <p className="text-sm"><span className="font-semibold text-[#1A242B]">{c.user_name}</span> <span className="text-xs text-[#8FA89B]">{(c.created_at || "").slice(0, 10)}</span></p>
              <p className="text-sm text-[#4A5D6B]">{c.body}</p>
            </div>
            {user && (c.user_id === user.id || ["admin", "staff"].includes(user.role)) && <button onClick={() => del(c.id)} className="text-[#8FA89B] hover:text-red-500"><Trash2 size={14} /></button>}
          </div>
        ))}
        {comments?.length === 0 && <p className="text-sm text-[#8FA89B]">Be the first to share.</p>}
      </div>
    </div>
  );
}

function StarRating({ avg, count, size = 16, interactive, value, onChange }) {
  const display = interactive ? (value || 0) : Math.round(avg || 0);
  return (
    <div className="flex items-center gap-1.5 mt-2" data-testid="star-rating">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} size={size}
            onClick={interactive ? () => onChange(n) : undefined}
            className={`${interactive ? "cursor-pointer" : ""} ${n <= display ? "fill-[#D98E73] text-[#D98E73]" : "text-[#E5E0D8]"}`} />
        ))}
      </div>
      {!interactive && <span className="text-sm text-[#4A5D6B]">{(avg || 0).toFixed(1)} ({count})</span>}
    </div>
  );
}

function CourseReviews({ course, enrolled }) {
  const qc = useQueryClient();
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const { data } = useQuery({ queryKey: ["reviews", course.id], queryFn: () => api.get(`/courses/${course.id}/reviews`).then((r) => r.data) });

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/courses/${course.id}/reviews`, { rating, body });
      setBody("");
      toast.success("Thanks for your review!");
      qc.invalidateQueries({ queryKey: ["reviews", course.id] });
      qc.invalidateQueries({ queryKey: ["course", course.id] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-8 border-t border-[#E5E0D8] pt-6" data-testid="course-reviews">
      <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2">
        <Star size={18} className="fill-[#D98E73] text-[#D98E73]" /> Reviews
        {data?.review_count > 0 && <span className="text-sm text-[#4A5D6B] font-sans">{data.avg_rating.toFixed(1)} · {data.review_count}</span>}
      </h3>
      {enrolled && (
        <div className="eyw-card p-4 mt-4">
          <p className="text-sm font-medium text-[#1A242B] mb-1">Rate this course</p>
          <StarRating interactive value={rating} onChange={setRating} size={22} />
          <textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder="What did you take away? (optional)" data-testid="review-body" className="eyw-input w-full resize-none mt-3" />
          <div className="flex justify-end mt-2">
            <button onClick={submit} disabled={saving} data-testid="review-submit" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={14} />} Submit review</button>
          </div>
        </div>
      )}
      <div className="space-y-4 mt-5">
        {(data?.reviews || []).map((r) => (
          <div key={r.id} className="flex gap-3" data-testid={`review-${r.id}`}>
            {r.user_avatar ? <img src={r.user_avatar} alt={r.user_name} className="h-9 w-9 rounded-full object-cover" /> :
              <span className="grid place-items-center h-9 w-9 rounded-full bg-[#144F75]/10 text-[#144F75] text-sm font-semibold">{(r.user_name || "?")[0]}</span>}
            <div>
              <p className="text-sm font-semibold text-[#1A242B]">{r.user_name}</p>
              <div className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} className={n <= r.rating ? "fill-[#D98E73] text-[#D98E73]" : "text-[#E5E0D8]"} />)}</div>
              {r.body && <p className="text-sm text-[#4A5D6B] mt-1">{r.body}</p>}
            </div>
          </div>
        ))}
        {data?.reviews?.length === 0 && <p className="text-sm text-[#8FA89B]">No reviews yet.</p>}
      </div>
    </div>
  );
}
